import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { demoProjects } from '../demo/projects';
import { useProject } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import './AdminLayout.css';

// 一级导航图标（统一线条风格，16px）
const icons: Record<string, React.ReactNode> = {
  overview: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="3" y="1.8" width="10" height="12.4" rx="1.2" />
      <path d="M5.5 5h5M5.5 7.5h5M5.5 10h3" />
    </svg>
  ),
  analysis: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M8 1.8 14 5 8 8.2 2 5Z" />
      <path d="M2 8l6 3.2L14 8" />
      <path d="M2 11l6 3.2L14 11" />
    </svg>
  ),
  strategy: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="8" r="6.2" />
      <circle cx="8" cy="8" r="3" />
      <circle cx="8" cy="8" r="0.4" fill="currentColor" />
    </svg>
  ),
  review: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M8 1.5 13.5 3.5v4c0 3.2-2.3 5.9-5.5 7-3.2-1.1-5.5-3.8-5.5-7v-4Z" />
      <path d="M5.6 7.8l1.7 1.7 3.1-3.1" />
    </svg>
  ),
  generate: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M9.5 1.8 11 5l3.2 1.5L11 8 9.5 11.2 8 8 4.8 6.5 8 5Z" />
      <path d="M3.5 9.5 4.2 11l1.5.7-1.5.7-.7 1.5-.7-1.5L1.3 11.7l1.5-.7Z" />
    </svg>
  ),
  account: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <circle cx="8" cy="5.2" r="2.7" />
      <path d="M2.5 13.8c.6-2.8 2.8-4.3 5.5-4.3s4.9 1.5 5.5 4.3" />
    </svg>
  ),
  provider: (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="1.8" y="3" width="12.4" height="10" rx="1.2" />
      <circle cx="4.6" cy="6.2" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="4.6" cy="9.8" r="0.9" fill="currentColor" stroke="none" />
      <path d="M7.6 6.2h6M7.6 9.8h6" />
    </svg>
  ),
};

// 一级导航：产品基础信息 → AI拆解 → AI策略 → AI审核 → AI生成
const mainNavItems = [
  { path: '/overview', label: '产品基础信息', icon: icons.overview },
  { path: '/analysis', label: 'AI拆解', icon: icons.analysis },
  { path: '/strategy', label: 'AI策略', icon: icons.strategy },
  { path: '/review', label: 'AI审核', icon: icons.review },
  { path: '/generate', label: 'AI生成', icon: icons.generate },
];

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [productOpen, setProductOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { currentProject, switchProject } = useProject();
  const { user, logout } = useAuth();

  // AI生成使用 iframe 全宽工作台，左侧导航自动折叠为图标栏；离开恢复
  const sidebarCollapsed = location.pathname === '/generate';

  // 路由变化时收起所有弹出层

  const contentRef = useRef<HTMLElement>(null);

  // 路由变化时收起弹出层，并将主内容区滚动位置归零（不继承上一页滚动）
  useEffect(() => {
    setProductOpen(false);
    setUserMenuOpen(false);
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  const handleSelectProject = (projectId: string) => {
    switchProject(projectId);
    setProductOpen(false);
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/', { replace: true });
  };

  const accountNav = user && user.role === 'admin'
    ? [
        { path: '/providers', label: 'AI接口管理', icon: icons.provider },
        { path: '/accounts', label: '账号管理', icon: icons.account },
      ]
    : [];

  const renderNavItem = (item: { path: string; label: string; icon: React.ReactNode }) => (
    <li
      key={item.path}
      className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
      onClick={() => navigate(item.path)}
      title={item.label}
    >
      {item.icon}
      <span className="nav-label">{item.label}</span>
    </li>
  );

  return (
    <div className="demo-layout">
      <header className="demo-header">
        <div className="header-left">
          <div className="logo" title="快推科技 · 智能投放平台">
            <img className="logo-img" src="/logo.png?v=2" alt="快推科技" />
          </div>
          <div className="product-selector">
            <div className="product-select-trigger" onClick={() => setProductOpen(!productOpen)}>
              <span className="ps-label">当前项目</span>
              <span className="ps-name">{currentProject.name}</span>
              <span className={`ps-arrow ${productOpen ? 'open' : ''}`}>▾</span>
            </div>
            {productOpen && (
              <div className="product-dropdown">
                {demoProjects.map(project => (
                  <div
                    key={project.id}
                    className={`product-dropdown-item ${project.id === currentProject.id ? 'active' : ''}`}
                    onClick={() => handleSelectProject(project.id)}
                  >
                    <div className="pdi-row">
                      <span className="pdi-check">{project.id === currentProject.id ? '✓' : ''}</span>
                      <span className="pdi-name">{project.name}</span>
                    </div>
                    <span className="dropdown-item-category">{project.category} · {project.currentStage}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="header-right">
          <div className="platform-tag">
            <span>快推 AI · 智能投放平台</span>
          </div>
          <div className="user-menu-wrap">
            <button
              className={`user-trigger ${userMenuOpen ? 'open' : ''}`}
              onClick={() => setUserMenuOpen(o => !o)}
            >
              <span className="user-avatar">{(user?.username || '?').slice(0, 1).toUpperCase()}</span>
              <span className="user-trigger-name">{user?.username}</span>
              <span className="arrow">▼</span>
            </button>
            {userMenuOpen && (
              <div className="user-menu">
                <div className="user-menu-head">
                  <span className="user-avatar user-avatar-lg">{(user?.username || '?').slice(0, 1).toUpperCase()}</span>
                  <div className="user-menu-info">
                    <p className="user-menu-name">{user?.username}</p>
                    <p className="user-menu-role">{user?.role === 'admin' ? '管理员' : '普通用户'}</p>
                  </div>
                </div>
                <button className="user-menu-item" onClick={handleLogout}>
                  退出登录
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <div className="demo-main">
        <nav className={`left-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <ul className="sidebar-nav sidebar-nav-main">
            {mainNavItems.map(renderNavItem)}
          </ul>
          <ul className="sidebar-nav sidebar-nav-bottom">
            {accountNav.map(renderNavItem)}
          </ul>
        </nav>
        <main ref={contentRef} className="content-area">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
